/* import __COLOCATED_TEMPLATE__ from './ai-score-panel.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import {
  type EntityScoreData,
  type EntityScoreIssue,
  type RuleResult,
} from 'embercom/components/content-readiness/types';

interface Signature {
  Args: {
    scoreData: EntityScoreData;
  };
}

const MAX_VISIBLE_ITEMS = 3;

export default class AiScorePanel extends Component<Signature> {
  @service declare intl: IntlService;

  @tracked showAllRules = false;
  @tracked showAllIssues = false;

  get ruleResultEntries(): Array<RuleResult & { key: string }> {
    let ruleResults = this.args.scoreData?.rule_results;
    if (!ruleResults) {
      return [];
    }
    return Object.entries(ruleResults).map(([key, result]) => ({
      key,
      ...result,
    }));
  }

  get visibleRuleResultEntries(): Array<RuleResult & { key: string }> {
    if (this.showAllRules) {
      return this.ruleResultEntries;
    }
    return this.ruleResultEntries.slice(0, MAX_VISIBLE_ITEMS);
  }

  get hasMoreRules(): boolean {
    return this.ruleResultEntries.length > MAX_VISIBLE_ITEMS;
  }

  get allIssues(): EntityScoreIssue[] {
    return this.ruleResultEntries.flatMap((entry) => entry.issues);
  }

  get visibleIssues(): EntityScoreIssue[] {
    if (this.showAllIssues) {
      return this.allIssues;
    }
    return this.allIssues.slice(0, MAX_VISIBLE_ITEMS);
  }

  get hasMoreIssues(): boolean {
    return this.allIssues.length > MAX_VISIBLE_ITEMS;
  }

  get totalIssueCount(): number {
    return this.ruleResultEntries.reduce((sum, entry) => sum + entry.issue_count, 0);
  }

  get progressBarColorClass(): string {
    let priority = this.args.scoreData?.priority;
    if (priority === 'P0') {
      return 'bg-vis-salmon-50';
    }
    if (priority === 'P1') {
      return 'bg-vis-yellow-50';
    }
    if (priority === 'P2') {
      return 'bg-vis-slateblue-50';
    }
    if (priority === 'P3') {
      return 'bg-vis-blue-50';
    }
    return 'bg-vis-green-50';
  }

  get priorityBadgeClass(): string {
    let priority = this.args.scoreData?.priority;
    if (priority === 'P0') {
      return 'bg-vis-salmon-50 text-on-error';
    }
    if (priority === 'P1') {
      return 'bg-vis-yellow-50 text-on-notice';
    }
    if (priority === 'P2') {
      return 'bg-vis-slateblue-50 text-on-info';
    }
    if (priority === 'P3') {
      return 'bg-vis-blue-50 text-on-info';
    }
    return 'bg-vis-green-50 text-on-success';
  }

  get priorityLabel(): string {
    let priority = this.args.scoreData?.priority;
    if (priority === 'P0') {
      return this.intl.t(
        'knowledge-hub.content-editor.content-readiness.ai-score-panel.priority.critical',
      );
    }
    if (priority === 'P1') {
      return this.intl.t(
        'knowledge-hub.content-editor.content-readiness.ai-score-panel.priority.poor',
      );
    }
    if (priority === 'P2') {
      return this.intl.t(
        'knowledge-hub.content-editor.content-readiness.ai-score-panel.priority.needs-work',
      );
    }
    if (priority === 'P3') {
      return this.intl.t(
        'knowledge-hub.content-editor.content-readiness.ai-score-panel.priority.good',
      );
    }
    if (priority === 'P4') {
      return this.intl.t(
        'knowledge-hub.content-editor.content-readiness.ai-score-panel.priority.excellent',
      );
    }
    return '';
  }

  statusColorClass = (status: string): string => {
    if (status === 'pass') {
      return 'text-success';
    }
    if (status === 'warn') {
      return 'text-notice';
    }
    return 'text-error';
  };

  statusLabel = (status: string): string => {
    if (status === 'pass') {
      return this.intl.t(
        'knowledge-hub.content-editor.content-readiness.ai-score-panel.status.pass',
      );
    }
    if (status === 'warn') {
      return this.intl.t(
        'knowledge-hub.content-editor.content-readiness.ai-score-panel.status.warn',
      );
    }
    return this.intl.t('knowledge-hub.content-editor.content-readiness.ai-score-panel.status.fail');
  };

  @action
  toggleShowAllRules() {
    this.showAllRules = !this.showAllRules;
  }

  @action
  toggleShowAllIssues() {
    this.showAllIssues = !this.showAllIssues;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'ContentReadiness::AiScorePanel': typeof AiScorePanel;
    'content-readiness/ai-score-panel': typeof AiScorePanel;
  }
}
